import { HugeiconsIcon } from '@hugeicons/react';
import {
  Add01Icon,
  Loading02Icon,
  AlertCircleIcon,
  RefreshIcon,
  Edit02Icon,
  Delete02Icon,
  UserGroupIcon,
  UserMultipleIcon,
  UserAdd01Icon,
  Cancel01Icon,
  ArrowLeft01Icon,
  InformationCircleIcon,
  Search01Icon,
} from '@hugeicons/core-free-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// GroupsManager — gestión de grupos de usuarios de VIA Insights (RF-7.1, CA-14).
// Un grupo agrupa usuarios para concederles acceso a agentes. "Administradores"
// es un grupo normal (sembrado por la migración 064), no un bypass por rol: su
// primer admin debe agregarse como miembro para ver agentes en /insights.
//
// Endpoints (prefijo real /api lo añade el cliente axios):
//   GET    /insights/groups                     -> [InsightsGroup] (con member_count)
//   POST   /insights/groups {name, description} -> InsightsGroup
//   GET    /insights/groups/{id}                -> {group, members}
//   PUT    /insights/groups/{id} {name, desc}   -> InsightsGroup
//   DELETE /insights/groups/{id}                -> {ok:true}
//   POST   /insights/groups/{id}/members {user_id}
//   DELETE /insights/groups/{id}/members/{userId}
//   GET    /users/search?q=                     -> [{id,name,email,title,department,initials}]

// Debounce reutilizable para el buscador de usuarios (mín. 2 chars, ~250ms).
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_CHARS = 2;

// Buscador de usuarios: Popover + Command con búsqueda server-side (GET
// /users/search). Al elegir un usuario invoca onPick(user). No mantiene estado
// de selección: es un disparador de "agregar".
function UserSearchPicker({ onPick, disabledIds = [], placeholder = 'Buscar usuario por nombre o correo…' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const q = query.trim();
    if (timer.current) clearTimeout(timer.current);
    if (q.length < SEARCH_MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/users/search', { params: { q } });
        setResults(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(apiError(e) || 'No se pudo buscar usuarios');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query]);

  const disabledSet = useMemo(() => new Set(disabledIds), [disabledIds]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-start text-muted-foreground sm:w-96">
          <HugeiconsIcon icon={UserAdd01Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
          Agregar miembro…
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(28rem,calc(100vw-2rem))] p-0" align="start">
        {/* shouldFilter=false: el filtrado es server-side (GET /users/search). */}
        <Command shouldFilter={false}>
          <CommandInput placeholder={placeholder} value={query} onValueChange={setQuery} />
          <CommandList>
            {loading ? (
              <div
                className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground"
                role="status"
                aria-live="polite">
                <HugeiconsIcon
                  icon={Loading02Icon}
                  size={16}
                  strokeWidth={1.5}
                  className="animate-spin"
                  aria-hidden="true"
                />
                Buscando…
              </div>
            ) : (
              <>
                <CommandEmpty>
                  {query.trim().length < SEARCH_MIN_CHARS ? 'Escribe al menos 2 caracteres' : 'Sin resultados'}
                </CommandEmpty>
                {results.map((u) => {
                  const yaMiembro = disabledSet.has(u.id);
                  return (
                    <CommandItem
                      key={u.id}
                      value={u.id}
                      disabled={yaMiembro}
                      onSelect={() => {
                        if (yaMiembro) return;
                        onPick(u);
                        setOpen(false);
                        setQuery('');
                      }}
                      className="items-start">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-foreground">{u.name || u.id}</span>
                        {u.email ? (
                          <span className="mt-0.5 block truncate text-xs text-muted-foreground">{u.email}</span>
                        ) : null}
                      </span>
                      {yaMiembro ? <span className="ml-2 text-xs text-muted-foreground">ya es miembro</span> : null}
                    </CommandItem>
                  );
                })}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Vista de miembros de un grupo: carga GET /insights/groups/{id}, lista los
// miembros y permite agregar (POST .../members) y quitar (DELETE .../members/{userId}).
function GroupMembersView({ group, onBack, onMembersChanged }) {
  const queryClient = useQueryClient();
  const [detail, setDetail] = useState({ group, members: [] });
  const [state, setState] = useState({ loading: true, error: '' });

  // Tras alta/baja de miembro, invalida la lista de agentes disponibles que ve
  // el usuario final (/insights). Así, cuando un admin se agrega a sí mismo al
  // grupo "Administradores", su selector refleja los agentes sin recargar.
  const invalidarAgentesDisponibles = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['insights', 'agents', 'available'] });
  }, [queryClient]);

  const load = useCallback(async () => {
    setState({ loading: true, error: '' });
    try {
      const { data } = await api.get(`/insights/groups/${group.id}`);
      setDetail({ group: data?.group || group, members: Array.isArray(data?.members) ? data.members : [] });
      setState({ loading: false, error: '' });
    } catch (e) {
      setState({ loading: false, error: apiError(e) });
    }
  }, [group]);

  useEffect(() => {
    load();
  }, [load]);

  const memberIds = useMemo(() => detail.members.map((m) => m.user_id), [detail.members]);

  const agregarMiembro = async (u) => {
    try {
      await api.post(`/insights/groups/${group.id}/members`, { user_id: u.id });
      toast.success(`${u.name || 'Usuario'} agregado al grupo`);
      await load();
      onMembersChanged?.();
      invalidarAgentesDisponibles();
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo agregar el miembro');
    }
  };

  const quitarMiembro = async (m) => {
    try {
      await api.delete(`/insights/groups/${group.id}/members/${m.user_id}`);
      toast.success(`${m.name || 'Usuario'} quitado del grupo`);
      await load();
      onMembersChanged?.();
      invalidarAgentesDisponibles();
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo quitar el miembro');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 rounded-md px-1 py-1 text-sm text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <HugeiconsIcon icon={ArrowLeft01Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
          Volver a grupos
        </button>
      </div>

      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <HugeiconsIcon icon={UserGroupIcon} size={18} strokeWidth={1.5} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-foreground">{detail.group?.name}</p>
          {detail.group?.description ? (
            <p className="mt-0.5 text-sm text-muted-foreground">{detail.group.description}</p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium text-foreground">Miembros</Label>
        <UserSearchPicker onPick={agregarMiembro} disabledIds={memberIds} />
      </div>

      {state.loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground" role="status" aria-live="polite">
          <HugeiconsIcon icon={Loading02Icon} size={18} strokeWidth={1.5} className="animate-spin" aria-hidden="true" />
          Cargando miembros…
        </div>
      ) : state.error ? (
        <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          <HugeiconsIcon icon={AlertCircleIcon} size={18} strokeWidth={1.5} aria-hidden="true" />
          <span className="flex-1">{state.error}</span>
          <button type="button" onClick={load} className="inline-flex items-center gap-1 font-medium hover:underline">
            <HugeiconsIcon icon={RefreshIcon} size={14} strokeWidth={1.5} aria-hidden="true" />
            Reintentar
          </button>
        </div>
      ) : detail.members.length === 0 ? (
        <p
          className="rounded-lg border border-dashed border-border px-3 py-4 text-sm text-muted-foreground"
          role="status">
          Este grupo aún no tiene miembros. Agrega usuarios con el buscador de arriba.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {detail.members.map((m) => (
            <li
              key={m.user_id}
              className="flex items-center gap-3 rounded-lg border border-border/80 bg-card px-3 py-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                {m.initials || (m.name ? m.name.slice(0, 2).toUpperCase() : '?')}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{m.name || m.user_id}</p>
                {m.email ? <p className="truncate text-xs text-muted-foreground">{m.email}</p> : null}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => quitarMiembro(m)}
                className="h-8 text-destructive hover:text-destructive"
                aria-label={`Quitar a ${m.name || 'usuario'} del grupo`}>
                <HugeiconsIcon icon={Delete02Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
                Quitar
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function GroupsManager() {
  const [groups, setGroups] = useState([]);
  const [state, setState] = useState({ loading: true, error: '' });
  const [openGroup, setOpenGroup] = useState(null); // grupo cuyos miembros se gestionan

  // Diálogo crear/renombrar.
  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState(null); // null => crear; grupo => renombrar
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Diálogo confirmar eliminación.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, error: '' });
    try {
      const { data } = await api.get('/insights/groups');
      setGroups(Array.isArray(data) ? data : []);
      setState({ loading: false, error: '' });
    } catch (e) {
      setState({ loading: false, error: apiError(e) });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const abrirCrear = () => {
    setFormTarget(null);
    setFormName('');
    setFormDescription('');
    setFormOpen(true);
  };

  const abrirRenombrar = (group) => {
    setFormTarget(group);
    setFormName(group.name || '');
    setFormDescription(group.description || '');
    setFormOpen(true);
  };

  const guardarGrupo = async (e) => {
    e?.preventDefault();
    const nombre = formName.trim();
    if (!nombre) {
      toast.error('El nombre del grupo es obligatorio');
      return;
    }
    setSaving(true);
    const body = { name: nombre, description: formDescription.trim() || undefined };
    try {
      if (formTarget) {
        await api.put(`/insights/groups/${formTarget.id}`, body);
        toast.success('Grupo actualizado');
      } else {
        await api.post('/insights/groups', body);
        toast.success('Grupo creado');
      }
      setFormOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo guardar el grupo');
    } finally {
      setSaving(false);
    }
  };

  const confirmarEliminar = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/insights/groups/${deleteTarget.id}`);
      toast.success('Grupo eliminado');
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e) || 'No se pudo eliminar el grupo');
    } finally {
      setDeleting(false);
    }
  };

  // Vista de miembros de un grupo concreto.
  if (openGroup) {
    return (
      <Card>
        <CardContent className="p-4">
          <GroupMembersView group={openGroup} onBack={() => setOpenGroup(null)} onMembersChanged={load} />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Hint de arranque (A.5): el primer admin debe agregarse al grupo
          "Administradores" para que /insights le liste agentes (no hay bypass por rol). */}
      <div
        className="flex items-start gap-2 rounded-lg border border-secondary/30 bg-secondary/10 p-3 text-sm text-foreground"
        role="note">
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={18}
          strokeWidth={1.5}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-secondary"
        />
        <span>
          El acceso a los agentes se concede por grupo o por persona. Si eres administrador y tu selector de VIA
          Insights aparece vacío, agrégate como miembro del grupo <strong>Administradores</strong> para ver los agentes
          ejecutables.
        </span>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Recargar grupos">
          <HugeiconsIcon icon={RefreshIcon} size={14} strokeWidth={1.5} aria-hidden="true" />
          Recargar
        </button>
        <Button type="button" onClick={abrirCrear}>
          <HugeiconsIcon icon={Add01Icon} size={18} strokeWidth={1.5} aria-hidden="true" />
          Nuevo grupo
        </Button>
      </div>

      {state.loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground" role="status" aria-live="polite">
          <HugeiconsIcon icon={Loading02Icon} size={18} strokeWidth={1.5} className="animate-spin" aria-hidden="true" />
          Cargando grupos…
        </div>
      ) : state.error ? (
        <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          <HugeiconsIcon icon={AlertCircleIcon} size={18} strokeWidth={1.5} aria-hidden="true" />
          <span className="flex-1">{state.error}</span>
          <button type="button" onClick={load} className="font-medium hover:underline">
            Reintentar
          </button>
        </div>
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-14 text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <HugeiconsIcon icon={UserGroupIcon} size={24} strokeWidth={1.5} aria-hidden="true" />
          </div>
          <p className="font-medium text-foreground">Aún no hay grupos</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Crea el primero para agrupar usuarios y concederles acceso a agentes.
          </p>
          <Button type="button" onClick={abrirCrear} className="mt-4">
            <HugeiconsIcon icon={Add01Icon} size={18} strokeWidth={1.5} aria-hidden="true" />
            Nuevo grupo
          </Button>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {groups.map((g) => (
            <li key={g.id}>
              <Card className="h-full">
                <CardContent className="flex h-full flex-col gap-3 p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <HugeiconsIcon icon={UserGroupIcon} size={18} strokeWidth={1.5} aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-foreground">{g.name}</p>
                      {g.description ? (
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{g.description}</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <HugeiconsIcon icon={UserMultipleIcon} size={14} strokeWidth={1.5} aria-hidden="true" />
                    {g.member_count} {g.member_count === 1 ? 'miembro' : 'miembros'}
                  </div>
                  <div className="mt-auto flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setOpenGroup(g)}>
                      <HugeiconsIcon icon={UserMultipleIcon} size={16} strokeWidth={1.5} aria-hidden="true" />
                      Miembros
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      onClick={() => abrirRenombrar(g)}
                      aria-label={`Renombrar grupo ${g.name}`}>
                      <HugeiconsIcon icon={Edit02Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
                      Renombrar
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(g)}
                      aria-label={`Eliminar grupo ${g.name}`}>
                      <HugeiconsIcon icon={Delete02Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
                      Eliminar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {/* Diálogo crear/renombrar grupo */}
      <Dialog open={formOpen} onOpenChange={(abierto) => !abierto && setFormOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={guardarGrupo}>
            <DialogHeader>
              <DialogTitle>{formTarget ? 'Renombrar grupo' : 'Nuevo grupo'}</DialogTitle>
              <DialogDescription>
                {formTarget
                  ? 'Actualiza el nombre o la descripción del grupo.'
                  : 'Crea un grupo para agrupar usuarios y concederles acceso a agentes.'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4 py-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="group-name">Nombre</Label>
                <Input
                  id="group-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ej.: Administradores"
                  autoFocus
                  maxLength={256}
                  autoComplete="off"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="group-description">Descripción (opcional)</Label>
                <Input
                  id="group-description"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Para qué sirve este grupo"
                  maxLength={1000}
                  autoComplete="off"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving || !formName.trim()}>
                {saving && (
                  <HugeiconsIcon
                    icon={Loading02Icon}
                    size={16}
                    strokeWidth={1.5}
                    className="mr-2 animate-spin"
                    aria-hidden="true"
                  />
                )}
                {formTarget ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Diálogo confirmar eliminación (acción destructiva) */}
      <Dialog open={deleteTarget != null} onOpenChange={(abierto) => !abierto && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar grupo</DialogTitle>
            <DialogDescription>
              ¿Seguro que quieres eliminar el grupo <strong>{deleteTarget?.name}</strong>? Se quitarán sus miembros y
              los accesos a agentes concedidos por este grupo. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmarEliminar}
              disabled={deleting}
              className="gap-2">
              {deleting && (
                <HugeiconsIcon
                  icon={Loading02Icon}
                  size={16}
                  strokeWidth={1.5}
                  className="animate-spin"
                  aria-hidden="true"
                />
              )}
              <HugeiconsIcon icon={Delete02Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
